import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useNearContext } from '../contexts/NearContext.jsx';

const defaultValues = {
  title: '',
  description: '',
  choices: 'Yes\nNo',
  minStake: 0.1,
  initialStake: 0.1,
  initialChoice: 'Yes',
};

const parseChoices = (rawChoices) =>
  Array.from(
    new Set(
      rawChoices
        .split('\n')
        .map((entry) => entry.trim())
        .filter(Boolean)
    )
  );

const coerceNumericId = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'bigint') {
    return Number(value);
  }

  const stringValue = typeof value === 'string' ? value : String(value);
  if (!stringValue) {
    return null;
  }

  const parsed = parseInt(stringValue, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const decodeBase64Safe = (value) => {
  if (!value) return null;
  try {
    if (typeof globalThis !== 'undefined' && typeof globalThis.atob === 'function') {
      return globalThis.atob(value);
    }
  } catch (error) {
    // Ignore and try fallback
  }

  try {
    if (typeof globalThis !== 'undefined' && globalThis.Buffer) {
      return globalThis.Buffer.from(value, 'base64').toString('utf-8');
    }
  } catch (error) {
    // Ignore final failure
  }

  return null;
};

const parseDecodedIdentifier = (decoded) => {
  if (!decoded) {
    return null;
  }

  const direct = coerceNumericId(decoded);
  if (direct !== null) {
    return direct;
  }

  try {
    const parsed = JSON.parse(decoded);
    if (typeof parsed === 'number' || typeof parsed === 'string' || typeof parsed === 'bigint') {
      return coerceNumericId(parsed);
    }
    if (parsed && typeof parsed === 'object') {
      for (const key of ['wager_id', 'id', 'value', 'result']) {
        const candidate = coerceNumericId(parsed[key]);
        if (candidate !== null) {
          return candidate;
        }
      }
    }
  } catch (error) {
    // Not JSON — ignore
  }

  return null;
};

const extractIdentifierFromStatus = (status) => {
  if (!status) {
    return null;
  }

  if (typeof status === 'number' || typeof status === 'string' || typeof status === 'bigint') {
    return coerceNumericId(status);
  }

  if (typeof status === 'object') {
    const raw = status.SuccessValue ?? status.successValue ?? null;
    if (typeof raw === 'string') {
      return parseDecodedIdentifier(decodeBase64Safe(raw));
    }
    if (Array.isArray(raw)) {
      for (const entry of raw) {
        const candidate = parseDecodedIdentifier(decodeBase64Safe(entry));
        if (candidate !== null) {
          return candidate;
        }
      }
    }
  }

  return null;
};

const extractWagerIdFromResult = (result) => {
  const direct = coerceNumericId(result);
  if (direct !== null) {
    return direct;
  }

  if (!result || typeof result !== 'object') {
    return null;
  }

  for (const key of ['wager_id', 'id', 'result', 'value']) {
    const candidate = coerceNumericId(result[key]);
    if (candidate !== null) {
      return candidate;
    }
  }

  const fromStatus = extractIdentifierFromStatus(result.status);
  if (fromStatus !== null) {
    return fromStatus;
  }

  const txStatus = extractIdentifierFromStatus(result.transaction_outcome?.outcome?.status);
  if (txStatus !== null) {
    return txStatus;
  }

  const receiptIds = result.transaction_outcome?.outcome?.receipt_ids;
  const receipts = Array.isArray(result.receipts_outcome) ? result.receipts_outcome : [];

  if (Array.isArray(receiptIds) && receipts.length > 0) {
    for (let index = receiptIds.length - 1; index >= 0; index -= 1) {
      const receiptId = receiptIds[index];
      const receipt = receipts.find((entry) => entry.id === receiptId);
      const receiptCandidate = extractIdentifierFromStatus(receipt?.outcome?.status);
      if (receiptCandidate !== null) {
        return receiptCandidate;
      }
    }
  }

  return null;
};

const resolveCreatedWagerId = async (result, contractApi) => {
  const direct = extractWagerIdFromResult(result);
  if (direct !== null) {
    return direct;
  }

  if (!contractApi?.getActiveWagers) {
    return null;
  }

  try {
    const latest = await contractApi.getActiveWagers(0, 1);
    if (Array.isArray(latest) && latest.length > 0) {
      const [first] = latest;
      const candidate =
        coerceNumericId(first?.id) ??
        coerceNumericId(first?.wager_id) ??
        coerceNumericId(first?.request?.id);
      if (candidate !== null) {
        return candidate;
      }
    }
  } catch (error) {
    console.warn('Unable to determine newly created wager ID from contract data:', error);
  }

  return null;
};

const CreateWager = () => {
  const navigate = useNavigate();
  const {
    isSignedIn,
    contractApi,
    executeTransaction,
    parseNearAmount,
  } = useNearContext();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onBlur',
    defaultValues,
  });

  const rawChoices = watch('choices');
  const choiceList = useMemo(() => parseChoices(rawChoices), [rawChoices]);

  const onSubmit = async (values) => {
    if (!isSignedIn) {
      toast.error('Please connect your NEAR wallet before creating a wager');
      return;
    }

    if (!contractApi) {
      toast.error('Contract not ready—please reload the page and try again.');
      return;
    }

    const choices = parseChoices(values.choices);
    if (choices.length < 2) {
      toast.error('Provide at least two unique choices.');
      return;
    }

    if (!choices.includes(values.initialChoice)) {
      toast.error('Your initial choice must match one of the listed options.');
      return;
    }

    if (Number(values.initialStake) < Number(values.minStake)) {
      toast.error('Your initial stake must be at least the minimum stake.');
      return;
    }

    const wagerRequest = {
      title: values.title.trim(),
      description: values.description.trim(),
      choices,
      min_stake: parseNearAmount(values.minStake.toString()),
    };

    try {
      const result = await executeTransaction(
        () =>
          contractApi.createWager(
            wagerRequest,
            values.initialChoice,
            values.initialStake
          ),
        'Wager created successfully!'
      );

      if (!result) {
        return;
      }

      const newWagerId = await resolveCreatedWagerId(result, contractApi);

      reset(defaultValues);

      if (newWagerId !== null && newWagerId !== undefined && !Number.isNaN(newWagerId)) {
        navigate(`/wager/${newWagerId}`, { replace: true });
      } else {
        navigate('/my-wagers');
      }
    } catch (error) {
      console.error('Failed to create wager:', error);
      // executeTransaction surfaces toast, but we can add fallback context
      toast.error(error.message || 'Failed to create wager');
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-gradient mb-2">Create a new wager</h1>
      <p className="text-gray-600 mb-8">
        Define the matchup, set your stakes, and invite others to take the opposite side. Funds stay in the NearBets smart contract until the outcome is resolved.
      </p>

      {!isSignedIn && (
        <div className="card border-red-200 bg-red-50 text-red-700 mb-6">
          Connect your wallet to deploy a wager. You can explore active wagers without connecting.
        </div>
      )}

      <form className="card space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700" htmlFor="title">
            Title
          </label>
          <input
            id="title"
            type="text"
            placeholder="Who wins the weekend showdown?"
            className="input-field"
            {...register('title', { required: 'Title is required', minLength: { value: 3, message: 'Title must be at least 3 characters' } })}
          />
          {errors.title && (
            <p className="text-sm text-red-500">{errors.title.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700" htmlFor="description">
            Description
          </label>
          <textarea
            id="description"
            rows={4}
            placeholder="Capture the full context, ground rules, and how a winner is decided."
            className="input-field"
            {...register('description', { required: 'Description is required', minLength: { value: 10, message: 'Add more detail so participants know what they are betting on.' } })}
          />
          {errors.description && (
            <p className="text-sm text-red-500">{errors.description.message}</p>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700" htmlFor="minStake">
              Minimum stake (NEAR)
            </label>
            <input
              id="minStake"
              type="number"
              step="0.01"
              min="0.1"
              className="input-field"
              {...register('minStake', { required: true, min: { value: 0.1, message: 'Minimum stake must be at least 0.1 NEAR' } })}
            />
            {errors.minStake && (
              <p className="text-sm text-red-500">{errors.minStake.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700" htmlFor="initialStake">
              Your initial stake (NEAR)
            </label>
            <input
              id="initialStake"
              type="number"
              step="0.01"
              min="0.1"
              className="input-field"
              {...register('initialStake', { required: true, min: { value: 0.1, message: 'Stake must be at least 0.1 NEAR' } })}
            />
            {errors.initialStake && (
              <p className="text-sm text-red-500">{errors.initialStake.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700" htmlFor="initialChoice">
              Your initial side
            </label>
            <select
              id="initialChoice"
              className="input-field"
              {...register('initialChoice', { required: true })}
              disabled={choiceList.length === 0}
            >
              {choiceList.length === 0 ? (
                <option value="">Add choices first</option>
              ) : (
                choiceList.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700" htmlFor="choices">
            Choices (one per line)
          </label>
          <textarea
            id="choices"
            rows={3}
            className="input-field"
            placeholder={'Team A\nTeam B'}
            {...register('choices', { required: true })}
          />
          <p className="text-xs text-gray-500">
            Unique options only—duplicates are removed automatically. You currently have{' '}
            <span className="font-semibold">{choiceList.length}</span> choice(s).
          </p>
        </div>

        <div className="flex items-center justify-end gap-4">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => reset(defaultValues)}
            disabled={isSubmitting}
          >
            Reset form
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting || !isSignedIn}
          >
            {isSubmitting ? 'Submitting…' : 'Launch wager'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateWager;
